import React, { useCallback, useState } from 'react';
import { Box, PaginationBar, useResponsive } from '@common-fe/common-fe';

import { useGetFileUrl, useGetPreviewForLMNs } from '@/modules/MyDocuments/hooks';
import { LettersOfMedicalNecessity, LMNClaim } from '@/modules/MyDocuments/myDocuments.types';
import useGetLettersOfMedicalNecessityQuery from '@/modules/MyDocuments/queries/useGetLettersOfMedicalNecessity.query';

import { TransactionLinkedLmnListEmptyState } from './TransactionLinkedLmnListEmptyState';
import TransactionLinkedLmnListItem from './TransactionLinkedLmnListItem';
import { TransactionLinkedLmnListLoader } from './TransactionLinkedLmnListLoader';

const PAGE_SIZE = 3;

interface Props {
  claimId?: string;
  employeeId?: string;
  dateOfService?: string;
  selectedLmnId?: string;
  setSelectedLmnId: (lmnId: string) => void;
}

const TransactionLinkedLmnList: React.FC<Props> = ({
  claimId,
  employeeId,
  dateOfService,
  selectedLmnId,
  setSelectedLmnId,
}) => {
  const [page, setPage] = useState(1);
  const { isMobile } = useResponsive();

  const { total, lettersOfMedicalNecessity, isLoading } = useGetLettersOfMedicalNecessityQuery({
    employeeId,
    validOn: dateOfService,
    disabled: !employeeId || !dateOfService,
    perPage: PAGE_SIZE,
    page,
  });

  const { valuesMap } = useGetPreviewForLMNs(lettersOfMedicalNecessity);
  const { getStableFileUrl } = useGetFileUrl({ valuesMap });

  const getIsLmnLinked = useCallback((lettersOfMedicalNecessity: LettersOfMedicalNecessity) => {
    return !!lettersOfMedicalNecessity?.claims?.find((linkedClaim: LMNClaim) => linkedClaim?.claimId === claimId);
  }, [claimId]);

  if (isLoading) {
    return TransactionLinkedLmnListLoader;
  }

  if (!lettersOfMedicalNecessity?.length) {
    return TransactionLinkedLmnListEmptyState;
  }

  return (
    <Box data-testid="transaction-linked-lmn-list_wrapper">
      {lettersOfMedicalNecessity?.map((letterOfMedicalNecessity) => (
        <TransactionLinkedLmnListItem
          key={letterOfMedicalNecessity?.id}
          letterOfMedicalNecessity={letterOfMedicalNecessity}
          fileUrl={getStableFileUrl(letterOfMedicalNecessity?.documentId)}
          isLinked={getIsLmnLinked(letterOfMedicalNecessity)}
          isSelected={letterOfMedicalNecessity?.id === selectedLmnId}
          onSelect={setSelectedLmnId}
          isMobile={isMobile}
        />
      ))}

      <PaginationBar
        wrapperStyles={{ padding: '0px' }}
        page={page}
        total={total}
        pageSize={PAGE_SIZE}
        onChangePage={setPage}
        hidePageSize
        hideJumpToPage
        hideTotalResult
      />
    </Box>
  );
};

export default TransactionLinkedLmnList;
